"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Home,
  Newspaper,
  Megaphone,
  BookOpen,
  Calendar,
  Info,
  Phone,
  Mail,
  Users,
  GraduationCap,
  Building2,
  FileText,
  Search,
  Image as ImageIcon,
  Bell,
  LayoutGrid,
} from "lucide-react";

type IconCmp = React.ComponentType<{ size?: number; className?: string }>;

/** A resolved bottom-nav tab supplied by the server (from the
 *  `mobile-bottom` Navigation menu). `icon` is a free CMS string. */
export interface MobileNavItem {
  href: string;
  icon?: string;
  label: { en: string; mm: string };
}

/** Design of the bar, chosen in Content Settings → Footer → Mobile
 *  Footer Navigation:
 *   • traditional   — edge-to-edge fixed bar, top hairline, with labels
 *                     (UM1 style).
 *   • floating      — detached rounded pill, margins all round, glass
 *                     surface, with labels (current Epona style).
 *   • half-floating — flush to the bottom edge, side margins only, top
 *                     corners rounded, glass surface, NO labels. */
export type MobileNavVariant = "traditional" | "floating" | "half-floating";

interface MobileBottomNavProps {
  currentLanguage?: "en" | "mm";
  /**
   * Admin-configured tabs from the `mobile-bottom` Navigation menu
   * (resolved server-side in ThemeLayout). Empty/undefined → fall back
   * to the built-in defaults so the bar never disappears for tenants
   * that haven't configured it.
   */
  items?: MobileNavItem[];
  /** Design variant (Settings.footer.mobileNavStyle). Default floating. */
  variant?: MobileNavVariant;
}

/**
 * Map a CMS `icon` string to a lucide component. Normalised to
 * lowercase alphanumerics so "Book Open" / "book-open" / "bookOpen"
 * all resolve. Unknown names fall back to a neutral grid icon.
 */
const ICON_MAP: Record<string, IconCmp> = {
  home: Home,
  news: Newspaper,
  newspaper: Newspaper,
  announcement: Megaphone,
  announcements: Megaphone,
  notice: Megaphone,
  notices: Megaphone,
  megaphone: Megaphone,
  bell: Bell,
  library: BookOpen,
  book: BookOpen,
  bookopen: BookOpen,
  event: Calendar,
  events: Calendar,
  calendar: Calendar,
  about: Info,
  info: Info,
  contact: Phone,
  phone: Phone,
  mail: Mail,
  email: Mail,
  staff: Users,
  faculty: Users,
  users: Users,
  people: Users,
  student: GraduationCap,
  students: GraduationCap,
  graduation: GraduationCap,
  graduationcap: GraduationCap,
  department: Building2,
  departments: Building2,
  building: Building2,
  page: FileText,
  file: FileText,
  document: FileText,
  search: Search,
  gallery: ImageIcon,
  image: ImageIcon,
  photo: ImageIcon,
};

function resolveIcon(name?: string): IconCmp {
  if (!name) return LayoutGrid;
  const key = name.toLowerCase().replace(/[^a-z0-9]/g, "");
  return ICON_MAP[key] ?? LayoutGrid;
}

/**
 * Mobile-app-style bottom tab bar for epona.
 *
 * Fixed to the bottom of the viewport on screens < lg. Hidden on
 * desktop where the regular header nav is used. Renders 4 tabs:
 * Home / News / Announcements / Library — the most common
 * destinations for an everyday visitor. The active tab is
 * highlighted based on the current pathname.
 *
 * Safe-area inset bottom padding handles iOS home-bar overlap;
 * `ThemeLayout` adds `pb-16` (or equivalent) to main on mobile so
 * page content isn't covered.
 */
type Tab = {
  href: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: { en: string; mm: string };
  // Active match: exact pathname OR pathname startsWith(matchPrefix).
  matchPrefix?: string;
};

const TABS: Tab[] = [
  {
    href: "/",
    icon: Home,
    label: { en: "Home", mm: "ပင်မ" },
  },
  {
    href: "/post/news",
    icon: Newspaper,
    label: { en: "News", mm: "သတင်း" },
    matchPrefix: "/post/news",
  },
  {
    href: "/post/announcements",
    icon: Megaphone,
    label: { en: "Notices", mm: "ကြေညာချက်များ" },
    matchPrefix: "/post/announcements",
  },
  {
    href: "/library",
    icon: BookOpen,
    label: { en: "Library", mm: "စာကြည့်တိုက်" },
    matchPrefix: "/library",
  },
];

/** Normalised tab the bar actually renders (icon already resolved). */
type RenderTab = {
  href: string;
  Icon: IconCmp;
  label: { en: string; mm: string };
  matchPrefix?: string;
};

function isActive(
  pathname: string,
  tab: { href: string; matchPrefix?: string },
): boolean {
  if (tab.href === "/" && pathname === "/") return true;
  const prefix = tab.matchPrefix ?? (tab.href !== "/" ? tab.href : undefined);
  if (
    prefix &&
    (pathname === prefix || pathname.startsWith(prefix + "/"))
  ) {
    return true;
  }
  return false;
}

export function MobileBottomNav({
  currentLanguage = "en",
  items,
  variant = "floating",
}: MobileBottomNavProps) {
  const pathname = usePathname() || "/";
  const lang = currentLanguage === "mm" ? "mm" : "en";

  // Admin-configured items win; otherwise fall back to the built-in
  // defaults. Cap at 5 so the bar stays usable on narrow screens.
  const resolved: RenderTab[] =
    items && items.length > 0
      ? items.slice(0, 5).map((it) => ({
          href: it.href || "/",
          Icon: resolveIcon(it.icon),
          label: it.label,
        }))
      : TABS.map((t) => ({
          href: t.href,
          Icon: t.icon,
          label: t.label,
          matchPrefix: t.matchPrefix,
        }));

  // Labels are dropped on the compact half-floating bar (icons only).
  const showLabels = variant !== "half-floating";

  // Per-variant container geometry + surface. Glass surface (blur +
  // translucent) for the two floating styles; a solid bar for the
  // traditional edge-to-edge one. iOS safe-area handled per variant:
  // the floating bar offsets `bottom`, the flush bars pad the bottom.
  const base =
    "lg:hidden fixed z-40 overflow-hidden bg-background/70 backdrop-blur-xl border border-white/40";
  let navClass: string;
  let navStyle: React.CSSProperties;
  if (variant === "traditional") {
    navClass =
      "lg:hidden fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-background shadow-[0_-6px_22px_-10px_rgba(9,30,60,0.28)]";
    navStyle = { paddingBottom: "env(safe-area-inset-bottom, 0px)" };
  } else if (variant === "half-floating") {
    // Flush to the bottom edge, side margins only, top corners rounded.
    navClass = `${base} left-3 right-3 bottom-0 rounded-t-[1.4rem] border-b-0 shadow-[0_-12px_34px_-14px_rgba(9,30,60,0.5)]`;
    navStyle = { paddingBottom: "env(safe-area-inset-bottom, 0px)" };
  } else {
    // Floating pill — detached from every edge, fully rounded.
    navClass = `${base} left-3 right-3 rounded-[1.6rem] shadow-[0_18px_44px_-14px_rgba(9,30,60,0.5),0_2px_8px_-2px_rgba(9,30,60,0.12)]`;
    navStyle = { bottom: "calc(0.6rem + env(safe-area-inset-bottom, 0px))" };
  }

  return (
    <nav
      aria-label="Primary mobile navigation"
      className={navClass}
      style={navStyle}
    >
      <ul
        className={`flex items-stretch justify-around gap-0.5 px-2 ${
          showLabels ? "py-2" : "py-2.5"
        }`}
      >
        {resolved.map((tab) => {
          const active = isActive(pathname, tab);
          const Icon = tab.Icon;
          return (
            <li key={tab.href} className="flex-1">
              <Link
                href={tab.href}
                aria-current={active ? "page" : undefined}
                aria-label={!showLabels ? tab.label[lang] : undefined}
                className="group flex h-full w-full flex-col items-center justify-center gap-1 rounded-2xl py-1 transition-transform active:scale-90"
              >
                {/* Material-3-style active indicator — a filled pill behind
                    the icon that fills with the brand colour when active. */}
                <span
                  className="flex h-8 w-[3.35rem] items-center justify-center rounded-full transition-all duration-200 group-active:scale-95"
                  style={
                    active
                      ? { backgroundColor: "var(--color-primary)" }
                      : undefined
                  }
                >
                  <Icon
                    size={21}
                    className={
                      active
                        ? "stroke-[2.2] text-white"
                        : "stroke-[1.8] text-muted-foreground transition-colors group-hover:text-foreground"
                    }
                  />
                </span>
                {showLabels && (
                  <span
                    className={`text-[10.5px] leading-none tracking-tight transition-colors ${
                      active
                        ? "font-semibold text-[color:var(--color-primary)]"
                        : "font-medium text-muted-foreground"
                    }`}
                  >
                    {tab.label[lang]}
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default MobileBottomNav;
